import { Configuration } from '@models/interfaces/configuration/index.interface';
import { MapButtonsSchema } from '@helpers/schemas/configuration/ui/tools/map-buttons';
import { DefaultConfig } from '@configuration/default-configuration';
import { SeatmapDisplaySchema } from '@helpers/schemas/configuration/ui/tools/seatmap-display';
import { NeighborsSchema } from '@helpers/schemas/configuration/ui/tools/neighbors';
import { SearchSchema } from '@helpers/schemas/configuration/ui/tools/search';
import { GeneralAdmissionSchema } from '@helpers/schemas/configuration/ui/tools/ga-exceptions';
import { TranslationSchema } from '@helpers/schemas/configuration/ui/tools/translation';
import { ActionButtonsSchema } from '@helpers/schemas/configuration/ui/tools/action-button';
import { DisclaimerSchema } from '@helpers/schemas/configuration/ui/tools/disclaimer';
import { UiConfiguration } from '@models/interfaces/configuration/UiConfiguration.interface';
import { ToastService } from '@services/toast.service';
import { SeatLevelDisplaySchema } from '@helpers/schemas/configuration/ui/tools/seat-level-display';
import { View3dDisplay } from '@helpers/schemas/configuration/ui/tools/view3d-display';
import { CustomPopupSchema } from '@helpers/schemas/configuration/ui/tools/custom-popup';
import { CustomAvailabilitySchema, RandomAvailabilitySchema } from '@helpers/schemas/configuration/ui/tools/availability';
import { LanguageTranslationSchema } from '@helpers/schemas/configuration/ui/tools/language-translations';
import { VenueConfigurationsSchema } from '@helpers/schemas/configuration/ui/tools/venue-configurations';
import { TitlesDisplaySchema } from '@helpers/schemas/configuration/ui/tools/titles-display';
import { LogoDisplaySchema } from '@helpers/schemas/configuration/ui/tools/logo-display';
import { ViewerMode } from '@models/enums/App/ViewerMode.enum';
import { ZodTypeAny } from 'zod';

const uiSchemas: Partial<Record<keyof Configuration['Ui'], ZodTypeAny>> = {
  MapButtons: MapButtonsSchema,
  SeatmapDisplay: SeatmapDisplaySchema,
  SeatLevelDisplay: SeatLevelDisplaySchema,
  View3dDisplay: View3dDisplay,
  Neighbors: NeighborsSchema,
  Search: SearchSchema,
  Translation: TranslationSchema,
  Disclaimer: DisclaimerSchema,
  GeneralAdmission: GeneralAdmissionSchema,
  ActionButtons: ActionButtonsSchema,
  LinkActionButtons: ActionButtonsSchema,
  CustomPopups: CustomPopupSchema,
  RandomAvailability: RandomAvailabilitySchema,
  CustomAvailability: CustomAvailabilitySchema,
  Languages: LanguageTranslationSchema,
  VenueConfigurations: VenueConfigurationsSchema,
  TitlesDisplay: TitlesDisplaySchema,
  LogoDisplay: LogoDisplaySchema,
};

function mergeWithDefaults<T>(raw: unknown, defaults: T): T {
  if (raw === undefined) return defaults;

  if (Array.isArray(defaults)) {
    return (Array.isArray(raw) ? raw : defaults) as T;
  }

  if (typeof defaults === 'object' && defaults !== null) {
    return { ...defaults, ...(raw as object) } as T;
  }

  return (raw ?? defaults) as T;
}

function assignValidated<K extends keyof UiConfiguration>(
  obj: Partial<UiConfiguration>,
  key: K,
  value: UiConfiguration[K]
) {
  obj[key] = value;
}

export function validateUiConfig(
  uiConfig: Partial<Configuration['Ui']>,
  toastService: ToastService,
  mode: ViewerMode
): Partial<Configuration['Ui']> {
  const validated: Partial<Configuration['Ui']> = {};
  const invalidKeys: string[] = [];

  // Loop through all known UI config sections and validate them
  for (const key of Object.keys(uiSchemas) as (keyof UiConfiguration)[]) {
    const schema = uiSchemas[key];
    if (!schema) continue;

    const defaultValue: UiConfiguration[typeof key] = DefaultConfig.Ui[key];
    const rawValue: UiConfiguration[typeof key] | undefined = uiConfig[key];

    if (rawValue === undefined) continue;

    // Merge user config with defaults, then validate against the schema
    const valueToValidate = mergeWithDefaults(rawValue, defaultValue);
    const result = schema.safeParse(valueToValidate);

    // If valid, keep parsed value; if invalid, fall back to defaults
    if (result.success) {
      assignValidated(validated, key, result.data);
    } else {
      assignValidated(validated, key, defaultValue);
      invalidKeys.push(key);      
    }
  }

  // Show toast if any invalid keys were found
  if (invalidKeys.length > 0 && mode === ViewerMode.Edit) {
    toastService.show(
      `Some UI config keys are invalid: ${invalidKeys.join(
        ', '
      )}. Using default values.`,
      'error',
      5000
    );
  }

  return validated;
}
